import React, { useState, useRef, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { 
  Box, 
  Paper, 
  TextField, 
  Button, 
  Typography, 
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Alert,
  Snackbar,
  Stepper,
  Step,
  StepLabel,
  LinearProgress,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import { Send, Lightbulb, Psychology, Chat, School } from '@mui/icons-material';
import { ChatMessage, QueryResponse, InterviewSummary } from '../types';
import APIService from '../services/api';
import { InsightsPanel } from './InsightsPanel';
import { QueryAttemptsPanel } from './QueryAttemptsPanel';
import { InterviewMode } from './InterviewMode';
import { IntelligentAgent } from './IntelligentAgent';
import { EnhancedChatBubble } from './EnhancedChatBubble';
import { AdvancedDashboard } from './AdvancedDashboard';

import { AdminBusinessRulesManager } from './AdminBusinessRulesManager';
import { EnhancedBusinessChat } from './EnhancedBusinessChat';

interface CEOChatbotProps {
  onQuery?: (query: string) => Promise<QueryResponse>;
}

export function CEOChatbot({ onQuery }: CEOChatbotProps) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
  const [processingStartTime, setProcessingStartTime] = useState<number | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  // Interview mode state
  const [mode, setMode] = useState<'chat' | 'interview' | 'advanced' | 'business-intelligence' | 'admin-rules'>('chat');
  const [interviewSummary, setInterviewSummary] = useState<InterviewSummary | null>(null);
  
  // New conversation state management
  const [conversationContext, setConversationContext] = useState<string>('');
  const [userRole, setUserRole] = useState<string>('CEO');
  
  // Enhanced conversation state tracking
  const [conversationStage, setConversationStage] = useState<'understanding' | 'exploring' | 'building' | 'executing' | 'complete'>('understanding');
  const [queryAttempts, setQueryAttempts] = useState<Array<{attempt: number, issue: string, solution?: string, executionTime?: number}>>([]);
  const [showLearningFeedback, setShowLearningFeedback] = useState(false);
  const [learningMessage, setLearningMessage] = useState('');

  // AI Agent state
  const [recentQueries, setRecentQueries] = useState<string[]>([]);
  const [showIntelligentAgent, setShowIntelligentAgent] = useState(true);
  const [showAdvancedFeatures, setShowAdvancedFeatures] = useState(false);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Helper function to get conversation stage index
  const getStageIndex = (stage: string) => {
    const stages = ['understanding', 'exploring', 'building', 'executing', 'complete'];
    return stages.indexOf(stage);
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Timeout warning effect
  useEffect(() => {
    if (isProcessing && processingStartTime) {
      const timeoutId = setTimeout(() => {
        if (isProcessing) {
          setShowTimeoutWarning(true);
        }
      }, 25000); // Show warning at 25 seconds (5 seconds before 30s timeout)

      return () => clearTimeout(timeoutId);
    }
  }, [isProcessing, processingStartTime]);

  const handleQuery = async (query: string) => {
    if (!query.trim()) return;

    setIsProcessing(true);
    setShowTimeoutWarning(false);
    setProcessingStartTime(Date.now());
    
    // Track recent queries for AI agent
    setRecentQueries(prev => [...prev.slice(-4), query]);
    
    // Reset conversation state for new query
    setConversationStage('understanding');
    setQueryAttempts([]);
    setShowLearningFeedback(false);
    
    // Simulate conversation stages progression
    setTimeout(() => setConversationStage('exploring'), 1000);
    setTimeout(() => setConversationStage('building'), 2000);
    setTimeout(() => setConversationStage('executing'), 3000);

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      type: 'user',
      text: query,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');

    try {
      let response: QueryResponse;
      
      if (onQuery) {
        response = await onQuery(query);
      } else {
        // Use smart routing to determine which endpoint to use
        response = await APIService.smartCeoChat(query, true);
      }
      
      // Update conversation context and user role from response
      if (response.businessContext) {
        setConversationContext(response.businessContext);
      }
      if (response.userRole) {
        setUserRole(response.userRole);
      }
      
      // Track conversation stages and learning
      if (response.responseType === 'clarification') {
        setConversationStage('building');
      } else if (response.responseType === 'success') {
        setConversationStage('complete');
        
        // Show learning feedback if available
        if (response.insights || response.suggestions) {
          setShowLearningFeedback(true);
          setLearningMessage('I\'ve learned from this interaction and will remember it for next time!');
        }
        
        // Simulate query attempts for demonstration (remove this in production)
        if (Math.random() > 0.7) { // 30% chance to show attempts
          setQueryAttempts([
            {
              attempt: 1,
              issue: 'Column "revenue" not found in table "Orders"',
              solution: 'Used "FinalGrandTotal" instead, which contains the revenue data'
            },
            {
              attempt: 2,
              issue: 'Table "Products" not joined, missing product information',
              solution: 'Added JOIN with Products table using ProductID relationship'
            },
            {
              attempt: 3,
              issue: 'Query executed successfully',
              solution: 'Found 1,247 orders with complete product and revenue data',
              executionTime: 234
            }
          ]);
        }
      }
      
      // Debug logging to see what the backend actually returns
      console.log('Backend Response:', response);
      console.log('Response fields:', {
        response: response.response,
        narrative: response.narrative,
        reply: response.reply,
        success: response.success
      });

      const assistantMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        text: response.response || response.narrative || response.reply || 'Query processed successfully.',
        timestamp: new Date(),
        data: response.results,
        sql: response.sql,
        insights: response.insights,
        
        // New advanced response fields
        responseType: response.responseType,
        suggestions: response.suggestions,
        selfHealingOptions: response.selfHealingOptions,
        businessContext: response.businessContext,
        userRole: response.userRole,
        followUpQuestions: response.followUpQuestions,
        examples: response.examples
      };

      setMessages(prev => [...prev, assistantMessage]);

      if (response.results && response.results.length > 0 && response.exportReady) {
        generateExcel(response.results, query);
      }
    } catch (error: any) {
      console.error('Chat error:', error);
      
      let errorMessage = 'An error occurred while processing your query.';
      let suggestion = '';
      
      // Handle specific error types
      if (error.response?.status === 408) {
        errorMessage = 'Request timed out. This query was too complex and took longer than 30 seconds.';
        suggestion = 'Try breaking it down into smaller questions or use the "Quick Questions" button for basic queries.';
      } else if (error.response?.status === 500) {
        errorMessage = 'Server error occurred. Please try again in a moment.';
        suggestion = 'If the problem persists, contact support.';
      } else if (error.code === 'ECONNABORTED') {
        errorMessage = 'Request timed out. The server took too long to respond.';
        suggestion = 'Try a simpler question or check your internet connection.';
      } else if (error.message) {
        errorMessage = `Error: ${error.message}`;
      }

      const errorChatMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        text: `${errorMessage}${suggestion ? `\n\n💡 **Suggestion**: ${suggestion}` : ''}`,
        timestamp: new Date(),
        isError: true
      };
      
      setMessages(prev => [...prev, errorChatMessage]);
    } finally {
      setIsProcessing(false);
      setProcessingStartTime(null);
      setShowTimeoutWarning(false);
    }
  };

  const handleEnhancedQuery = (query: string, context: any) => {
    console.log('Enhanced query with context:', context);
    handleQuery(query);
  };

  const handleRetry = (messageId: string) => {
    const message = messages.find(m => m.id === messageId);
    if (message && message.type === 'user') {
      handleQuery(message.text);
    }
  };

  const handleQuickQuestion = (question: string) => {
    setInput(question);
    handleQuery(question);
  };

  const generateExcel = (data: any[], queryName: string) => {
    try {
      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Results');
      const metadata = [
        ['Query', queryName],
        ['Generated', new Date().toISOString()],
        ['Total Rows', data.length],
        ['Generated By', 'SleekShop CEO Dashboard'],
        ['User', 'andrewngo@sleekshop.com']
      ];
      const metaSheet = XLSX.utils.aoa_to_sheet(metadata);
      XLSX.utils.book_append_sheet(wb, metaSheet, 'Metadata');
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
      saveAs(blob, `CEO_Report_${Date.now()}.xlsx`);
    } catch (error) {
      console.error('Excel generation failed:', error);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleQuery(input);
    }
  };

  const DataTable = ({ data }: { data: any[] }) => {
    if (!data || data.length === 0) return null;
    const columns = Object.keys(data[0] || {});
    return (
      <Box sx={{ mt: 2, overflowX: 'auto' }}>
        <Typography variant="h6" gutterBottom>
          Results ({data.length} rows)
        </Typography>
        <Paper sx={{ overflow: 'auto', maxHeight: 400 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#f5f5f5' }}>
                {columns.map((col) => (
                  <th key={col} style={{ padding: '8px', textAlign: 'left', border: '1px solid #ddd' }}>
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.slice(0, 10).map((row, idx) => (
                <tr key={idx}>
                  {columns.map((col) => (
                    <td key={col} style={{ padding: '8px', border: '1px solid #ddd' }}>
                      {typeof row[col] === 'number' ? (row[col] || 0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',') : String(row[col] || '')}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {data.length > 10 && (
            <Typography variant="body2" sx={{ p: 1, textAlign: 'center', color: 'text.secondary' }}>
              Showing first 10 of {data.length} rows
            </Typography>
          )}
        </Paper>
      </Box>
    );
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Mode Toggle */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', backgroundColor: 'background.paper' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <ToggleButtonGroup
            value={mode}
            exclusive
            onChange={(e, newMode) => {
              if (newMode !== null) {
                setMode(newMode);
                if (newMode === 'chat') {
                  // Show interview summary if available
                  if (interviewSummary) {
                    setShowLearningFeedback(true);
                    setLearningMessage(`Interview completed! I learned about ${interviewSummary.tablesDiscovered.length} tables and ${interviewSummary.businessRules.length} business rules. I'm now better equipped to help with your business questions.`);
                  }
                }
              }
            }}
            aria-label="chat mode"
            size="small"
          >
            <ToggleButton value="chat" aria-label="chat mode">
              <Chat sx={{ mr: 1 }} />
              Chat Mode
            </ToggleButton>
            <ToggleButton value="interview" aria-label="interview mode">
              <School sx={{ mr: 1 }} />
              Interview Mode
            </ToggleButton>
            <ToggleButton value="advanced" aria-label="advanced mode">
              <Psychology sx={{ mr: 1 }} />
              Advanced AI
            </ToggleButton>
            <ToggleButton value="business-intelligence" aria-label="business intelligence mode">
              <Lightbulb sx={{ mr: 1 }} />
              Business Intelligence
            </ToggleButton>
            <ToggleButton value="admin-rules" aria-label="admin rules mode">
              <Psychology sx={{ mr: 1 }} />
              Admin Rules
            </ToggleButton>
          </ToggleButtonGroup>
          
          {/* AI Agent Toggle */}
          {mode === 'chat' && (
            <Button
              variant={showIntelligentAgent ? 'contained' : 'outlined'}
              size="small"
              onClick={() => setShowIntelligentAgent(!showIntelligentAgent)}
              startIcon={<Psychology />}
            >
              {showIntelligentAgent ? 'Hide AI Agent' : 'Show AI Agent'}
            </Button>
          )}
        </Box>
        
        {interviewSummary && mode === 'chat' && (
          <Alert severity="info" sx={{ mt: 2 }} onClose={() => setInterviewSummary(null)}>
            <Typography variant="body2">
              🎓 <strong>Interview Knowledge Applied:</strong> I've learned about your database structure and can now provide more intelligent business insights!
            </Typography>
          </Alert>
        )}
      </Box>

      {/* Interview Mode */}
      {mode === 'interview' && (
        <InterviewMode
          onComplete={(summary) => {
            setInterviewSummary(summary);
            setMode('chat');
          }}
          onExit={() => setMode('chat')}
        />
      )}

      {/* Advanced AI Mode */}
      {mode === 'advanced' && (
        <AdvancedDashboard
          currentKnowledge={interviewSummary}
          recentQueries={recentQueries}
          showAdvancedFeatures={showAdvancedFeatures}
          onToggleFeatures={setShowAdvancedFeatures}
        />
      )}

      {/* Business Intelligence Mode */}
      {mode === 'business-intelligence' && (
        <EnhancedBusinessChat />
      )}

      {/* Admin Business Rules Manager */}
      {mode === 'admin-rules' && (
        <AdminBusinessRulesManager />
      )}

      {/* Chat Mode */}
      {mode === 'chat' && (
        <>
          {/* Timeout Warning Snackbar */}
          <Snackbar
            open={showTimeoutWarning}
            autoHideDuration={10000}
            onClose={() => setShowTimeoutWarning(false)}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <Alert 
              severity="warning" 
              onClose={() => setShowTimeoutWarning(false)}
              action={
                <Button color="inherit" size="small" onClick={() => setShowTimeoutWarning(false)}>
                  Dismiss
                </Button>
              }
            >
              ⏰ Complex query in progress. This may take up to 30 seconds to complete.
            </Alert>
          </Snackbar>

          <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
            {/* Intelligent AI Agent */}
            {showIntelligentAgent && mode === 'chat' && (
              <IntelligentAgent
                onTriggerInterview={() => setMode('interview')}
                onEnhancedQuery={handleEnhancedQuery}
                currentKnowledge={interviewSummary}
                recentQueries={recentQueries}
                userRole={userRole}
              />
            )}

            {/* Conversation Stage Stepper */}
            {messages.length > 0 && (
              <Box sx={{ mb: 2, p: 2, backgroundColor: 'background.paper', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Psychology sx={{ mr: 1, fontSize: '1rem', color: 'primary.main' }} />
                  Query Progress
                </Typography>
                <Stepper activeStep={getStageIndex(conversationStage)}>
                  <Step><StepLabel>Understanding</StepLabel></Step>
                  <Step><StepLabel>Exploring</StepLabel></Step>
                  <Step><StepLabel>Building</StepLabel></Step>
                  <Step><StepLabel>Executing</StepLabel></Step>
                  <Step><StepLabel>Complete</StepLabel></Step>
                </Stepper>
              </Box>
            )}
            
            {/* Learning Feedback */}
            {showLearningFeedback && (
              <Alert severity="success" sx={{ mb: 2 }} onClose={() => setShowLearningFeedback(false)}>
                <Typography variant="body2">
                  🧠 <strong>Learning in Progress:</strong> {learningMessage}
                </Typography>
              </Alert>
            )}
            
            {messages.length === 0 && (
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  {/* Role Indicator */}
                  {userRole !== 'general' && (
                    <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                      <Chip 
                        label={`${userRole.toUpperCase()} Mode`}
                        color="primary"
                        variant="outlined"
                        size="small"
                        sx={{ mr: 1 }}
                      />
                      {conversationContext && (
                        <Typography variant="caption" color="text.secondary">
                          Context: {conversationContext}
                        </Typography>
                      )}
                    </Box>
                  )}
                  
                  <Typography variant="h6" gutterBottom>
                    Welcome to SleekShop {userRole !== 'general' ? userRole : 'CEO'} Dashboard
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Ask me anything about your business. Try queries like:
                  </Typography>
                  <Box sx={{ mt: 1, mb: 2 }}>
                    <Chip 
                      label="Show me today's revenue" 
                      size="small" 
                      sx={{ mr: 1, mb: 1 }} 
                      onClick={() => handleQuickQuestion("Show me today's revenue")}
                      clickable
                    />
                    <Chip 
                      label="What are our top products?" 
                      size="small" 
                      sx={{ mr: 1, mb: 1 }} 
                      onClick={() => handleQuickQuestion("What are our top products?")}
                      clickable
                    />
                    <Chip 
                      label="Compare this week to last week" 
                      size="small" 
                      sx={{ mr: 1, mb: 1 }} 
                      onClick={() => handleQuickQuestion("Compare this week to last week")}
                      clickable
                    />
                    <Chip 
                      label="Show me problem orders" 
                      size="small" 
                      sx={{ mr: 1, mb: 1 }} 
                      onClick={() => handleQuickQuestion("Show me problem orders")}
                      clickable
                    />
                  </Box>
                  
                  {/* Interview Mode Promotion */}
                  {!interviewSummary && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      <Typography variant="body2">
                        🎓 <strong>New!</strong> Try <strong>Interview Mode</strong> to help me learn about your database structure. 
                        This will make all future business questions more intelligent and accurate!
                      </Typography>
                    </Alert>
                  )}
                  
                  {/* Quick Questions Section */}
                  <Box sx={{ mt: 2, p: 2, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
                    <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                      <Lightbulb sx={{ mr: 1, fontSize: '1rem' }} />
                      Quick Questions (Instant Answers)
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      <Chip 
                        label="Hello" 
                        size="small" 
                        variant="outlined"
                        onClick={() => handleQuickQuestion("Hello")}
                        clickable
                      />
                      <Chip 
                        label="What time is it?" 
                        size="small" 
                        variant="outlined"
                        onClick={() => handleQuickQuestion("What time is it?")}
                        clickable
                      />
                      <Chip 
                        label="Help" 
                        size="small" 
                        variant="outlined"
                        onClick={() => handleQuickQuestion("Help")}
                        clickable
                      />
                      <Chip 
                        label="Status" 
                        size="small" 
                        variant="outlined"
                        onClick={() => handleQuickQuestion("Status")}
                        clickable
                      />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            )}

            {messages.map((message) => (
              <Box
                key={message.id}
                sx={{
                  mb: 2,
                  display: 'flex',
                  justifyContent: message.type === 'user' ? 'flex-end' : 'flex-start'
                }}
              >
                <EnhancedChatBubble
                  message={message}
                  isUser={message.type === 'user'}
                  onRetry={() => handleRetry(message.id)}
                  onFeedback={(type) => {
                    console.log('Feedback received:', type);
                    // TODO: Implement feedback handling
                  }}
                  onCopy={() => {
                    navigator.clipboard.writeText(message.text);
                  }}
                  onShare={() => {
                    // TODO: Implement share functionality
                    console.log('Share message:', message.text);
                  }}
                  onBookmark={() => {
                    // TODO: Implement bookmark functionality
                    console.log('Bookmark message:', message.id);
                  }}
                  interviewKnowledge={interviewSummary}
                  showMetadata={true}
                />
                
                {/* Data Table */}
                {message.data && <DataTable data={message.data} />}
                
                {/* Advanced Insights Panel */}
                {message.type === 'assistant' && (
                  <InsightsPanel
                    insights={message.insights}
                    suggestions={message.suggestions}
                    followUpQuestions={message.followUpQuestions}
                    selfHealingOptions={message.selfHealingOptions}
                    businessContext={message.businessContext}
                    userRole={message.userRole}
                    examples={message.examples}
                    onFollowUpClick={(question: string) => {
                      setInput(question);
                      handleQuery(question);
                    }}
                    onSelfHealingClick={(option: string) => {
                      setInput(option);
                      handleQuery(option);
                    }}
                  />
                )}
                
                {/* Query Attempts Panel - Show when there are multiple attempts */}
                {queryAttempts.length > 1 && (
                  <QueryAttemptsPanel 
                    attempts={queryAttempts}
                    onShowDetails={(attempt) => {
                      // Could show SQL details in a modal or expand the card
                      console.log('Show attempt details:', attempt);
                    }}
                  />
                )}
              </Box>
            ))}
            <div ref={messagesEndRef} />
          </Box>

          <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                fullWidth
                multiline
                maxRows={4}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask anything: 'Show me top 10 products by revenue this month with export'"
                disabled={isProcessing}
                variant="outlined"
                size="small"
              />
              <Button
                variant="contained"
                onClick={() => handleQuery(input)}
                disabled={isProcessing || !input.trim()}
                sx={{ minWidth: 'auto', px: 2 }}
              >
                {isProcessing ? <CircularProgress size={20} /> : <Send />}
              </Button>
            </Box>
            
            {isProcessing && (
              <Box sx={{ mt: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="caption" color="text.secondary">
                    {conversationStage === 'understanding' && 'Understanding your question...'}
                    {conversationStage === 'exploring' && 'Exploring database schema...'}
                    {conversationStage === 'building' && 'Building the perfect query...'}
                    {conversationStage === 'executing' && 'Executing and analyzing...'}
                    {conversationStage === 'complete' && 'Finalizing results...'}
                    {processingStartTime && ` (${Math.floor((Date.now() - processingStartTime) / 1000)}s)`}
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="indeterminate" 
                  sx={{ height: 2 }}
                  color={conversationStage === 'complete' ? 'success' : 'primary'}
                />
              </Box>
            )}
          </Box>
        </>
      )}
    </Box>
  );
} 